import React, { FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import { Button } from '../../components/styled/Button/Button'
import { TimeHorizonGermanString, TimeHorizon } from '../../@types/timeHorizon'
import { Intent } from '../../@types/intent'
import { Progress } from '../../@types/progress'
import { reportTrackingEvent } from '../../tracking/thunks'
import { useReportViewEvent } from '../../hooks'
import { TimeHorizonStepIcon } from '../../components/icons/StepIcons/TimeHorizonStepIcon'
import { Flow, IntegrationType } from '../../@types/config'
import { ReduxState } from '../../store/reducers'
import { updateTimeHorizon } from '../../store/lead/leadSlice'

export interface TimeHorizonStepProps {
  goToNextStep: () => unknown
  goToPreviousStep: () => unknown
  progress: Progress
  displayBack: boolean
}

const UserIntentTitleString: Record<Intent, string> = {
  [Intent.MAKLER_SALE]: 'verkaufen',
  [Intent.INDEPENDENT_SALE]: 'verkaufen',
  [Intent.INDEPENDENT_WITH_OFFER_SALE]: 'verkaufen',
  [Intent.PARTIAL_SALE]: 'verkaufen',
  [Intent.MAYBE_AGENT_SALE]: 'verkaufen',
  [Intent.MAYBE_MAKLER_SALE]: 'verkaufen',
  [Intent.BUY]: 'kaufen',
  [Intent.BUY_MAYBE_WITH_SELL_OPTION]: 'kaufen',
  [Intent.BUY_WITH_SELL_OPTION]: 'kaufen',
  [Intent.RENT]: 'mieten',
  [Intent.LET]: 'vermieten',
  [Intent.MAKLER_LET]: 'vermieten',
  [Intent.MAYBE_MAKLER_LET]: 'vermieten',
  [Intent.INDEPENDENT_LET]: 'vermieten',
}

const UserIntentTitleStringDeeplink = {
  ['SELL']: 'verkaufen',
}

export const SOLAR_HEADLINE = 'Wann möchtest du voraussichtlich starten?'

export const TimeHorizonStep: FC<TimeHorizonStepProps> = (props) => {
  const {
    lead,
    config: { theme, integrationType, flow },
  } = useSelector((state: ReduxState) => state)
  const isIv24Integration = integrationType === IntegrationType.IMMOVERKAUF24

  const reportViewEventArgs = flow === Flow.SOLAR ? { pageTitle: 'starttime' } : { pageTitle: 'selltime' }
  useReportViewEvent(reportViewEventArgs)

  const getQueryParameters = () => location.search.substr(1)
  const parseParamValue = (paramName: string) => new URLSearchParams(getQueryParameters()).get(paramName)
  const intent = parseParamValue('intent')?.toUpperCase()

  const headline =
    flow === Flow.SOLAR
      ? SOLAR_HEADLINE
      : `Wann möchtest du voraussichtlich ${
          intent === 'SELL'
            ? (UserIntentTitleStringDeeplink['SELL'] as keyof typeof UserIntentTitleStringDeeplink)
            : UserIntentTitleString[lead.intent as keyof typeof UserIntentTitleString]
        }?`

  const dispatch = useDispatch()

  const goToNextStep = (timeHorizon: TimeHorizon) => {
    dispatch(updateTimeHorizon(timeHorizon))
    const reportEventArgs =
      flow === Flow.SOLAR
        ? {
            pageTitle: 'starttime',
            label: 'starttime_submit',
            eventName: 'lead_input',
            inputValue: `${timeHorizon}`,
            customDimensions: { ga_cd_lead_engine_input_value: timeHorizon },
            action: 'solar-leadengine',
            category: 'residential',
            flow: flow,
          }
        : {
            pageTitle: 'selltime',
            label: `selltime_submit`,
            inputValue: `${timeHorizon}`,
            eventName: 'lead_input',
            customDimensions: {
              ga_cd_lead_engine_input_value: timeHorizon,
            },
          }
    dispatch(reportTrackingEvent(reportEventArgs))
    props.goToNextStep()
  }

  return (
    <StepTemplate
      goToPreviousStep={props.goToPreviousStep}
      icon={<TimeHorizonStepIcon />}
      headline={headline}
      displayNext={false}
      progress={props.progress}
      displayBack={props.displayBack}
      backgroundColor={theme.background}
      textColor={theme.text}
    >
      {Object.entries(TimeHorizonGermanString).map(([key, label]) => (
        <Button
          key={key}
          text={label}
          onClick={() => goToNextStep(TimeHorizon[key as TimeHorizon])}
          className='margin-right-m margin-bottom-xl'
          dataTesting={`option-${key}`}
          backgroundColor={isIv24Integration ? theme.secondary : theme.background}
          color={isIv24Integration ? '#FFFFFF' : theme.text}
          borderColor={theme.secondary}
        />
      ))}
    </StepTemplate>
  )
}

export default TimeHorizonStep
